import {useContext} from 'react'

import {Button, Grid, Text} from '@primer/react-brand'

import {ContentfulSectionIntro} from '@github-ui/swp-core/components/contentful/ContentfulSectionIntro'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import type {GenericGroup, GenericContent, GenericSectionWithIds} from '../../../../brand/lib/types/contentful'

import {PlanTypeContext} from '../_context/PlanTypeContext'

import {PlanTypeSegmentedControl} from '../_components/PlanTypeSegmentedControl'
import {PricingPlans} from '../_components/PricingPlans/PricingPlans'
import IdeList from '../_components/IdeList'

type Props = {
  contentfulContent: GenericSectionWithIds
}

export function PricingSection(props: Props) {
  const {contentfulContent} = props

  const {sectionIntro} = contentfulContent.fields

  const {featuresCopilotPlanTypes, featuresCopilotPricingOptions, featuresCopilotPricingSectionIdeList} =
    contentfulContent.ids as {
      featuresCopilotPlanTypes: GenericContent
      featuresCopilotPricingOptions: GenericGroup
      featuresCopilotPricingSectionIdeList: GenericContent
    }

  const ideListCtaLink = featuresCopilotPricingSectionIdeList?.fields.links?.at(0)

  const {planType, setPlanType} = useContext(PlanTypeContext)

  return (
    <Grid as="section" id="pricing" className="pb-8 pb-md-12">
      <Grid.Column span={12}>
        {sectionIntro ? <ContentfulSectionIntro component={sectionIntro} fullWidth headingSize="3" /> : null}

        {featuresCopilotPlanTypes ? (
          <div className="d-flex flex-justify-center px-2 mb-7">
            <h2 className="visually-hidden" id="pricing-plans">
              {featuresCopilotPlanTypes.fields.heading || 'Pricing plans'}
            </h2>

            <PlanTypeSegmentedControl
              value={planType}
              onChange={setPlanType}
              contentfulContent={featuresCopilotPlanTypes}
            />
          </div>
        ) : null}

        {featuresCopilotPricingOptions ? <PricingPlans contentfulContent={featuresCopilotPricingOptions} /> : null}

        <div className="d-flex flex-justify-center pt-6 pb-2">
          {featuresCopilotPricingSectionIdeList ? (
            <Text as="p" size="200" variant="muted" weight="medium" align="center" className="mb-5">
              {featuresCopilotPricingSectionIdeList.fields.heading}
            </Text>
          ) : null}
        </div>

        <IdeList type="small" location="pricing-section" />

        {ideListCtaLink ? (
          <div className="d-flex flex-justify-center pt-4">
            <Button
              as="a"
              href={ideListCtaLink.fields.href}
              variant="subtle"
              {...getAnalyticsEvent({
                action: ideListCtaLink.fields.text,
                tag: 'button',
                context: 'pricing',
                location: 'ide_list',
              })}
            >
              {ideListCtaLink.fields.text}
            </Button>
          </div>
        ) : null}
      </Grid.Column>
    </Grid>
  )
}

try{ PricingSection.displayName ||= 'PricingSection' } catch {}